import { createSlice } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";

// Helper to check if a JWT token has expired or is about to expire
export const isTokenExpired = (token, offsetSeconds = 0) => {
  if (!token || typeof token !== "string") return true;
  try {
    const decoded = jwtDecode(token);
    if (!decoded || !decoded.exp) return true;
    return decoded.exp * 1000 <= Date.now() + offsetSeconds * 1000;
  } catch {
    return true;
  }
};

// Decode helper that extracts user claims safely
export const getUserFromToken = (token) => {
  if (!token || typeof token !== "string") return null;
  try {
    const decoded = jwtDecode(token);
    if (decoded) {
      // Normalize email in case backend sent a list or tuple
      const rawEmail = decoded.email;
      const normalizedEmail = Array.isArray(rawEmail)
        ? rawEmail[0]
        : rawEmail || "";

      return {
        ...decoded,
        email: normalizedEmail,
        id: decoded.sub
          ? isNaN(Number(decoded.sub))
            ? decoded.sub
            : Number(decoded.sub)
          : decoded.id,
      };
    }
    return null;
  } catch {
    return null;
  }
};

// Hydrate from query parameters or localStorage on load/refresh
const getInitialTokens = () => {
  let token = null;
  let refresh = null;

  if (typeof window !== "undefined") {
    try {
      const params = new URLSearchParams(window.location.search);
      const urlToken = params.get("token") || params.get("accessToken");
      const urlRefresh = params.get("refreshToken");

      if (urlToken) {
        token = urlToken;
        refresh = urlRefresh;
        localStorage.setItem("accessToken", urlToken);
        if (urlRefresh) {
          localStorage.setItem("refreshToken", urlRefresh);
        }
      }
    } catch (e) {
      console.warn("Failed to parse query params:", e);
    }
  }

  if (!token) {
    try {
      token = localStorage.getItem("accessToken");
      refresh = localStorage.getItem("refreshToken");
    } catch {
      // localStorage might not be available
    }
  }

  // If both tokens are expired, clean up storage immediately
  const accessExpired = isTokenExpired(token);
  const refreshExpired = isTokenExpired(refresh);

  if (accessExpired && refreshExpired) {
    if (typeof window !== "undefined") {
      try {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
      } catch {}
    }
    return { token: null, refresh: null };
  }

  return { token, refresh };
};

const { token: accessToken, refresh: refreshToken } = getInitialTokens();
const initialUser = getUserFromToken(accessToken);
const isAccessValid = !isTokenExpired(accessToken);
const isRefreshValid = !isTokenExpired(refreshToken);

const initialState = {
  accessToken,
  refreshToken,
  user: initialUser,
  assessmentId: typeof window !== "undefined" ? localStorage.getItem("assessmentId") : null,
  isAuthenticated: isAccessValid || isRefreshValid,
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action) => {
      const { access_token, refresh_token, user } = action.payload;
      state.accessToken = access_token;
      state.refreshToken = refresh_token;
      state.user = user || getUserFromToken(access_token);
      state.isAuthenticated = true;
      state.loading = false;
      state.error = null;

      if (access_token) {
        localStorage.setItem("accessToken", access_token);
      }
      if (refresh_token) {
        localStorage.setItem("refreshToken", refresh_token);
      }
      localStorage.setItem("lastActivityTime", String(Date.now()));
      // Always clear stale assessment ID on new login
      localStorage.removeItem("assessmentId");
      state.assessmentId = null;
    },
    loginFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload || "Login failed";
    },
    refreshTokenSuccess: (state, action) => {
      const { access_token } = action.payload;
      state.accessToken = access_token;
      const updatedUser = getUserFromToken(access_token);
      if (updatedUser) {
        state.user = updatedUser;
      }
      state.isAuthenticated = true;
      if (access_token) {
        localStorage.setItem("accessToken", access_token);
      }
    },
    logout: (state) => {
      state.accessToken = null;
      state.refreshToken = null;
      state.user = null;
      state.assessmentId = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;

      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("lastActivityTime");
      localStorage.removeItem("assessmentId");
    },
    setAssessmentId: (state, action) => {
      state.assessmentId = action.payload;
      if (action.payload) {
        localStorage.setItem("assessmentId", String(action.payload));
      } else {
        localStorage.removeItem("assessmentId");
      }
    },
    clearAssessmentId: (state) => {
      state.assessmentId = null;
      localStorage.removeItem("assessmentId");
    },
    setUser: (state, action) => {
      state.user = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  refreshTokenSuccess,
  logout,
  setAssessmentId,
  clearAssessmentId,
  setUser,
  clearError,
} = authSlice.actions;

export default authSlice.reducer;

// Selectors
export const selectAuth = (state) => state.auth;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUser = (state) => state.auth.user;
export const selectAssessmentId = (state) => state.auth.assessmentId;
export const selectAccessToken = (state) => state.auth.accessToken;
export const selectRefreshToken = (state) => state.auth.refreshToken;
